import axiosInstance from "@/lib/axios";
import { getApiBaseUrl } from "@/config/apiBase";
import {
  AIModelListItem,
  AIProviderRow,
  AIProviderType,
  AIServerEvent,
  AIWireMessage,
  DiscoveredAIModel,
} from "../types/ai.types";
import type {
  VoiceEngineId,
  VoiceEngineInfo,
  VoiceEnginesResponse,
  VoiceEngineVoice,
  VoiceSettings,
  VoiceSettingsPatch,
} from "../voice/telecaller.types";

// ---------------------------------------------------------------------------
// AI API client.
//
// `streamChat` opens a fetch-based stream to /ai/chat. We use fetch (not
// axios) because axios doesn't expose the raw response body as a readable
// stream — needed for SSE chunk parsing. Auth header is attached manually
// to match the axios interceptor pattern.
// ---------------------------------------------------------------------------

export async function listAIModels(): Promise<AIModelListItem[]> {
  const { data } = await axiosInstance.get("/ai/models");
  return (data?.data ?? []) as AIModelListItem[];
}

// ---------------------------------------------------------------------------
// Admin catalogue (/ai/admin/*) — PLATFORM STAFF ONLY.
//
// AIProvider / AIModel are platform-level tables: one API key and one model
// list shared by every tenant. The server gates these on
// SYSTEM_ADMIN / PLATFORM_STAFF (requireSystemAdmin); the settings UI mirrors
// that with selectIsSystemAdmin so a tenant admin never sees controls that
// would 403. Enabling a model here turns it on for EVERY AI-enabled tenant.
//
// The API key is write-only: responses carry `hasApiKey` + `apiKeyLast4`,
// never the secret itself.
// ---------------------------------------------------------------------------

export async function listAIProviderTypes(): Promise<AIProviderType[]> {
  const { data } = await axiosInstance.get("/ai/admin/providers/types");
  return (data?.data ?? []) as AIProviderType[];
}

export async function listAIProviders(): Promise<AIProviderRow[]> {
  const { data } = await axiosInstance.get("/ai/admin/providers");
  return (data?.data ?? []) as AIProviderRow[];
}

export async function createAIProvider(input: {
  type: AIProviderType;
  config: Record<string, unknown>;
}): Promise<AIProviderRow> {
  const { data } = await axiosInstance.post("/ai/admin/providers", input);
  return data?.data as AIProviderRow;
}

/** Send `config.apiKey` only when rotating — blank is treated as "keep existing". */
export async function updateAIProvider(
  id: number,
  input: { config?: Record<string, unknown>; isEnabled?: boolean }
): Promise<AIProviderRow> {
  const { data } = await axiosInstance.patch(`/ai/admin/providers/${id}`, input);
  return data?.data as AIProviderRow;
}

export async function deleteAIProvider(id: number): Promise<void> {
  await axiosInstance.delete(`/ai/admin/providers/${id}`);
}

/** Live list from the provider itself, using the key already stored server-side. */
export async function discoverAIModels(providerId: number): Promise<DiscoveredAIModel[]> {
  const { data } = await axiosInstance.get(`/ai/admin/providers/${providerId}/discover`);
  return (data?.data ?? []) as DiscoveredAIModel[];
}

export async function addAIModels(
  providerId: number,
  models: Array<{
    modelString: string;
    displayName: string;
    pricing: { in: number; out: number };
    contextWindow?: number;
    supportsTools?: boolean;
  }>
): Promise<void> {
  await axiosInstance.post("/ai/admin/models/bulk", { providerId, models });
}

export async function updateAIModel(
  id: number,
  input: { isEnabled?: boolean; displayName?: string; sortOrder?: number }
): Promise<void> {
  await axiosInstance.patch(`/ai/admin/models/${id}`, input);
}

export async function deleteAIModel(id: number): Promise<void> {
  await axiosInstance.delete(`/ai/admin/models/${id}`);
}

export interface StreamChatArgs {
  modelId: number;
  messages: AIWireMessage[];
  /** Reply language ("ml" default / "en"). */
  language?: "ml" | "en";
  signal?: AbortSignal;
  onEvent: (e: AIServerEvent) => void;
}

export async function streamChat(args: StreamChatArgs): Promise<void> {
  const token = localStorage.getItem("xale_access_token");
  const url = `${getApiBaseUrl()}/ai/chat`;
  const res = await fetch(url, {
    method: "POST",
    signal: args.signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ modelId: args.modelId, messages: args.messages, language: args.language ?? "ml" }),
  });

  if (!res.ok || !res.body) {
    let msg = `Chat failed (${res.status})`;
    try {
      const j = await res.json();
      msg = j?.message || msg;
    } catch { /* keep default */ }
    args.onEvent({ type: "error", message: msg });
    args.onEvent({ type: "done", stopReason: "error" });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  // SSE parser: events come as `event: <name>\ndata: <json>\n\n`.
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const lines = block.split("\n");
      let eventName = "message";
      let dataStr = "";
      for (const line of lines) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
      }
      if (!dataStr) continue;
      try {
        const data = JSON.parse(dataStr);
        args.onEvent({ type: eventName as any, ...data });
      } catch {
        // Drop malformed events; keep streaming.
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Voice — /ai/voice/*. The mic clip goes up as multipart; the reply comes
// back as raw audio bytes we hand straight to an <audio> element.
// ---------------------------------------------------------------------------

export async function transcribeVoiceClip(
  blob: Blob,
  signal?: AbortSignal
): Promise<string> {
  const form = new FormData();
  const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
  form.append("audio", blob, `voice.${ext}`);
  // Axios drops the JSON Content-Type for FormData so the browser can stamp
  // the multipart boundary itself.
  const { data } = await axiosInstance.post("/ai/voice/transcribe", form, {
    signal,
    timeout: 60000,
  });
  return String(data?.data?.text ?? "").trim();
}

export async function synthesiseSpeech(
  text: string,
  signal?: AbortSignal
): Promise<Blob> {
  const { data, headers } = await axiosInstance.post(
    "/ai/voice/speak",
    { text },
    { signal, responseType: "blob", timeout: 60000 }
  );
  const type = (headers?.["content-type"] as string | undefined) || "audio/wav";
  return data instanceof Blob ? data : new Blob([data], { type });
}

// ---------------------------------------------------------------------------
// Voice engines for the live call — Gemini built-in / Sarvam / ElevenLabs.
// ---------------------------------------------------------------------------

export async function listVoiceEngines(): Promise<VoiceEnginesResponse> {
  const { data } = await axiosInstance.get("/ai/voice/engines");
  return {
    engines: (data?.data?.engines ?? []) as VoiceEngineInfo[],
    defaults: data?.data?.defaults as VoiceEnginesResponse["defaults"],
  };
}

/** Settings → AI Connections → Voice for AI calls (per-workspace keys + default voice). */
export async function getVoiceSettings(): Promise<VoiceSettings> {
  const { data } = await axiosInstance.get("/ai/voice/settings");
  return data?.data?.settings as VoiceSettings;
}

/** A new key is checked with the provider before it is saved; null removes the saved key. */
export async function updateVoiceSettings(patch: VoiceSettingsPatch): Promise<VoiceSettings> {
  const { data } = await axiosInstance.patch("/ai/voice/settings", patch, { timeout: 30000 });
  return data?.data?.settings as VoiceSettings;
}

/** A blob error body (responseType "blob") → the server's message. */
async function blobErrorMessage(err: unknown, fallback: string): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body = (err as any)?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text());
      if (parsed?.message) return String(parsed.message);
    } catch {
      /* not JSON */
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return String((err as any)?.response?.data?.message ?? (err as Error)?.message ?? fallback);
}

/** One line in the picked voice — the sheet's ▶ button. Same pronunciation fixes as the call. */
export async function previewVoice(
  input: { engine: VoiceEngineId; voice: string; text: string; language: "ml" | "en"; companyName?: string; companySayAs?: string },
  signal?: AbortSignal
): Promise<Blob> {
  try {
    const { data, headers } = await axiosInstance.post("/ai/voice/preview", input, { signal, responseType: "blob", timeout: 60000 });
    const type = (headers?.["content-type"] as string | undefined) || "audio/wav";
    return data instanceof Blob ? data : new Blob([data], { type });
  } catch (err) {
    throw new Error(await blobErrorMessage(err, "Could not play the preview."));
  }
}

/** ElevenLabs instant clone owned by this workspace. `consent` must be true. */
export async function cloneVoice(input: {
  name: string;
  gender?: "female" | "male";
  description?: string;
  removeBackgroundNoise?: boolean;
  consent: boolean;
  files: File[];
}): Promise<VoiceEngineVoice & { requiresVerification?: boolean }> {
  const form = new FormData();
  form.append("name", input.name);
  if (input.gender) form.append("gender", input.gender);
  if (input.description) form.append("description", input.description);
  form.append("removeBackgroundNoise", String(!!input.removeBackgroundNoise));
  form.append("consent", String(input.consent));
  for (const f of input.files) form.append("files", f, f.name);
  const { data } = await axiosInstance.post("/ai/voice/elevenlabs/voices", form, { timeout: 180000 });
  return data?.data?.voice;
}

export async function deleteClonedVoice(voiceId: string): Promise<void> {
  await axiosInstance.delete(`/ai/voice/elevenlabs/voices/${encodeURIComponent(voiceId)}`);
}
